import { useCallback, useEffect, useRef, useState } from 'react';

import {
  CommandResultResponse,
  EmployeeResponse,
  KidsRatioOverviewResponse,
  PlanAppointmentsRequest,
  PlanningEntityType,
  PlanningResponse
} from 'generated';
import EventBus from 'lib/EventBus';
import { useGetAdminSettings } from 'queries';
import { UseMutateFunction } from 'react-query';
import { useRecoilState, useRecoilValue } from 'recoil';

import { PlanningEntity, PlanningEntityMap } from '../types';
import {
  PlanningEntitiesWebsocketBody,
  WebsocketBody
} from 'RestAPIs/activities/types';

import { getPlanUnitIds } from '../../../utils';
import {
  getClientsOrEmployeesList,
  getMatchingPlanningEntityIdentifiers,
  getPlanningByResponseEntities,
  getPlanningEntityTypeByView,
  isEqualToSessionCorrelationToken,
  normalizePlanningData,
  refetchPlanResponsePlanningEntityIdentifiers
} from '../utils';

import {
  CommandResult,
  ConnectAppointmentGroupingPayload,
  ConnectSchedulePayload
} from 'RestAPIs/activities';
import { mutations } from 'RestAPIs/rapini';

import {
  employeeEntityIdsFilterAtom,
  employeesAtom,
  kidsRatioAtom,
  planningAtom,
  planResponsePlanningEntityIdentifiersAtom,
  planUnitsAtom,
  selectedPlanningEntitiesAtom,
  View
} from '../atoms';

import {
  useConnectAppointmentGrouping,
  useConnectSchedule
} from '../mutations';
import usePlanningEntity from './usePlanningEntity';
import useSelectedDateRange from './useSelectedDateRange';
import { useGetPlanning } from '../queries';
import { useSelector } from '../../../../hooks';
import { useSessionStorage } from 'usehooks-ts';
import { LISTVIEW_SESSION_STORAGE_KEYS } from '../ListView/types';

export type UsePlanningReturn = {
  data: PlanningResponse | undefined;
  kidsRatio: KidsRatioOverviewResponse | null;
  isMyActivities?: boolean;
  planningEntities: PlanningEntityMap | null;
  setPlanningEntities: (value: any) => void;
  clonedPlanningEntities: PlanningEntityMap | null;
  setClonedPlanningEntities: (value: any) => void;
  planAppointments: UseMutateFunction<
    CommandResultResponse,
    unknown,
    PlanAppointmentsRequest,
    unknown
  >;
  connectAppointmentGrouping: UseMutateFunction<
    CommandResult,
    unknown,
    ConnectAppointmentGroupingPayload,
    unknown
  >;
  connectSchedule: UseMutateFunction<
    CommandResult,
    unknown,
    ConnectSchedulePayload,
    unknown
  >;
  revertAction: () => void;
  startDate: string;
  isLoading: boolean;
  employees: EmployeeResponse[];
  showEvents: boolean;
};

export default function usePlanning(
  view: View,
  showEvents: boolean
): UsePlanningReturn {
  const [planningEntityTypePreference] = usePlanningEntity();
  const planningEntityType = planningEntityTypePreference[view];

  const [kidsRatio, setKidsRatio] = useRecoilState(kidsRatioAtom);
  const [planningEntities, setPlanningEntities] = useRecoilState(planningAtom);

  const clonedPlanningEntitiesRef = useRef<PlanningEntityMap | null>(null);
  const setClonedPlanningEntities = useCallback(
    (value: PlanningEntityMap | null) => {
      clonedPlanningEntitiesRef.current = value;
    },
    []
  );

  const [dataAvailable, setDataAvailable] = useState(false);

  const { startDate, endDate } = useSelectedDateRange();
  const selectedPlanUnits = useRecoilValue(planUnitsAtom);
  const [employees, setEmployees] =
    useRecoilState<EmployeeResponse[]>(employeesAtom);
  const [employeeEntityIds] = useRecoilState(employeeEntityIdsFilterAtom);
  const [
    {
      ts: planResponseTimestamp,
      planningEntityIdentifiers: planResponsePlanningEntityIdentifiers
    },
    setPlanResponsePlanningEntityIdentifiers
  ] = useRecoilState(planResponsePlanningEntityIdentifiersAtom);
  const [selectedPlanningEntities] = useRecoilState(
    selectedPlanningEntitiesAtom
  );
  const [isLoading, setIsLoading] = useState(true);

  const { hasManageOwnProjectsAccess, hasProjectTeamAccess } = useSelector(
    ({ grants }) => grants
  );

  const planningEntitiesRef = useRef<PlanningEntityMap | null>(
    planningEntities
  );
  const selectedDateRangeRef = useRef({ startDate, endDate });
  const planUnitIdsRef = useRef<number[]>(getPlanUnitIds(selectedPlanUnits));
  const planningEntityTypeRef = useRef<PlanningEntityType>(planningEntityType);

  const [sessionStoredClientIds] = useSessionStorage<string[]>(
    LISTVIEW_SESSION_STORAGE_KEYS.FILTERED_CLIENTS,
    []
  );

  const { data: travelTimeSettings } = useGetAdminSettings(
    'activity-planning-travel-time'
  );
  const thresholdInMinutes = travelTimeSettings?.[0]?.settings.find(
    (s) => s.key === 'travel.time.show.above.x.minutes'
  )?.value;

  const cardsViewThreshold =
    view === (View.Cards || view === View.Roster) ? +thresholdInMinutes : 0;

  const currentPlanningEntityIdentifiers = [
    ...Object.values(planningEntities ?? {}).map(
      ({ planningEntityIdentifier }) => planningEntityIdentifier
    ),
    ...(employeeEntityIds ?? []).map((entityId) => ({
      entityId,
      planningEntityType: 'EMPLOYEE' as PlanningEntityType
    })),
    ...(selectedPlanningEntities ? selectedPlanningEntities : []) // Filters etc.
  ].filter((identifier) => identifier);

  const planUnitIds =
    !selectedPlanningEntities || selectedPlanningEntities?.length === 0
      ? getPlanUnitIds(selectedPlanUnits)
      : [];
  const planningEntityIdentifiers = getMatchingPlanningEntityIdentifiers(
    currentPlanningEntityIdentifiers,
    planResponsePlanningEntityIdentifiers,
    selectedPlanningEntities,
    planUnitIds
  );

  const isFetchEnabled = useCallback(() => {
    switch (true) {
      // Negaters
      case !startDate:
        return false;

      case planningEntityIdentifiers.length === 0 &&
        planUnitIds.length === 0 &&
        planningEntityType !== 'PROJECT':
        return false;

      // Valid statements
      case planningEntityType === 'PROJECT':
        return true;

      case (hasManageOwnProjectsAccess || hasProjectTeamAccess) &&
        planningEntityType === 'EMPLOYEE' &&
        selectedDateRangeRef.current.startDate === startDate &&
        selectedDateRangeRef.current.endDate === endDate:
        return true;

      case planningEntityType === 'CLIENT':
        if (view === View.List) {
          // UsePlanning should only trigger when there are selected planning entities or when there are session stored client ids
          return (
            planningEntityIdentifiers.length > 0 &&
            (sessionStoredClientIds.length === 0 ||
              (sessionStoredClientIds.length > 0 &&
                planningEntityIdentifiers.some(
                  ({ planningEntityType }) => planningEntityType === 'CLIENT'
                )))
          );
        }
        return selectedPlanUnits?.length > 0;

      case selectedPlanUnits?.length > 0 &&
        selectedDateRangeRef.current.startDate === startDate &&
        selectedDateRangeRef.current.endDate === endDate:
        return true;

      // All else is false
      default:
        return false;
    }
  }, [
    selectedPlanUnits,
    planningEntityType,
    selectedPlanningEntities,
    hasManageOwnProjectsAccess,
    hasProjectTeamAccess,
    view,
    sessionStoredClientIds,
    planningEntityIdentifiers
  ]);

  const {
    data,
    isLoading: queryLoading,
    isFetching,
    isError,
    refetch
  } = useGetPlanning(
    startDate as string,
    endDate as string,
    getPlanningEntityTypeByView(planningEntityType, view),
    planningEntityIdentifiers,
    {
      enabled: isFetchEnabled()
    },
    [planResponseTimestamp]
  );

  useEffect(() => {
    planningEntitiesRef.current = planningEntities;
    selectedDateRangeRef.current = { startDate, endDate };
    planUnitIdsRef.current = getPlanUnitIds(selectedPlanUnits);
    planningEntityTypeRef.current = planningEntityType;
  }, [
    selectedPlanUnits,
    startDate,
    endDate,
    planningEntities,
    planningEntityType
  ]);

  useEffect(() => {
    refetchPlanResponsePlanningEntityIdentifiers(
      setPlanResponsePlanningEntityIdentifiers,
      planResponseTimestamp
    );
  }, [selectedPlanUnits, startDate, endDate]);

  useEffect(() => {
    if (!isFetchEnabled()) setPlanningEntities(null);
  }, [planningEntityType, selectedPlanUnits]);

  useEffect(() => {
    if (data) {
      const normalizedPlanningEntities = normalizePlanningData(
        data,
        cardsViewThreshold
      );

      const isProjectCall = Object.values(
        normalizedPlanningEntities ?? {}
      ).some((entity: PlanningEntity) => entity.type === 'PROJECT');
      const isOrganizationalUnitCall = planningEntityIdentifiers.some(
        ({ planningEntityType }) => planningEntityType === 'ORGANIZATIONAL_UNIT'
      ); // Original request identifiers

      // On a page load (or in case of projects) we use an empty object to clear out any old entities, else we update existing entities
      const replacedPlanningEntities: PlanningEntityMap = {
        ...(isOrganizationalUnitCall || isProjectCall ? {} : planningEntities)
      };
      Object.keys(normalizedPlanningEntities).forEach((key) => {
        replacedPlanningEntities[key] = normalizedPlanningEntities[key];
      });

      setKidsRatio(data.kidsRatio ?? null);
      setPlanningEntities(replacedPlanningEntities);
      setClonedPlanningEntities(replacedPlanningEntities);

      const { employees } = getClientsOrEmployeesList(replacedPlanningEntities);

      setEmployees(employees);
      setDataAvailable(true);
    }
  }, [data]);

  useEffect(() => {
    const eventBusUrl = `/activity-planning`;

    if (dataAvailable) {
      if (EventBus.isRegistered(eventBusUrl, onPlanningUpdate)) return;
      EventBus.register(eventBusUrl, onPlanningUpdate);
    }

    return () => {
      if (!EventBus.isRegistered(eventBusUrl, onPlanningUpdate)) return;
      EventBus.unregister(eventBusUrl, onPlanningUpdate);
    };
  }, [dataAvailable]);

  const onPlanningUpdate = async (update) => {
    const { type, body } = update;
    const planningEntities = planningEntitiesRef.current;

    if (type === 'planningEntitiesUpdated' && planningEntities) {
      const b: PlanningEntitiesWebsocketBody = JSON.parse(body);

      const planningEntityIdentifiers = getMatchingPlanningEntityIdentifiers(
        Object.values(planningEntities)
          .filter(Boolean)
          .map(({ planningEntityIdentifier }) => planningEntityIdentifier),
        b.planningEntityIdentifiers,
        selectedPlanningEntities,
        planUnitIdsRef.current
      );

      if (
        isEqualToSessionCorrelationToken(b.correlationToken) ||
        planningEntityIdentifiers.length === 0
      ) {
        return;
      }

      const planningEntityMap = await getPlanningByResponseEntities({
        startDate: selectedDateRangeRef.current.startDate as string,
        endDate: selectedDateRangeRef.current.endDate as string,
        organizationalUnitPlanningEntityPerspective:
          getPlanningEntityTypeByView(planningEntityTypeRef.current, view),
        planningEntityIdentifiers,
        planningEntities,
        threshold: cardsViewThreshold
      });

      setPlanningEntities(planningEntityMap);
      setClonedPlanningEntities(planningEntityMap);

      const { employees } = getClientsOrEmployeesList(planningEntityMap);
      setEmployees(employees);

      return;
    }

    if (type === 'activityPlanningUpdated') {
      const b: WebsocketBody = JSON.parse(body);
      const selectedPlanUnitIds = getPlanUnitIds(selectedPlanUnits);

      if (
        b.date >= (startDate as string) &&
        selectedPlanUnitIds.some((id) => b.organizationalUnitIds.includes(id))
      ) {
        refetch();
      }
      return;
    }
  };

  const { mutate: planAppointments, isLoading: planAppointmentsLoading } =
    mutations.usePlanAppointments();

  const {
    mutate: connectAppointmentGrouping,
    isLoading: connectAppointmentLoading
  } = useConnectAppointmentGrouping({
    // TODO: note, remove when connectAppointmentGrouping triggers a websocket event returning a correlationToken
    onSuccess: () => {
      setPlanResponsePlanningEntityIdentifiers({
        ts: Date.now(),
        planningEntityIdentifiers: []
      });
    }
  });

  const { mutate: connectSchedule, isLoading: connectScheduleLoading } =
    useConnectSchedule({
      onSuccess: () => {
        setPlanResponsePlanningEntityIdentifiers({
          ts: Date.now(),
          planningEntityIdentifiers: []
        });
      }
    });

  const revertAction = () => {
    setPlanningEntities(clonedPlanningEntitiesRef.current);
  };

  useEffect(() => {
    if (
      !isError &&
      (isFetching ||
        queryLoading ||
        planAppointmentsLoading ||
        connectAppointmentLoading ||
        connectScheduleLoading)
    ) {
      // Show the loader when data is fetching
      setIsLoading(true);
    } else if (
      !isFetching &&
      !planAppointmentsLoading &&
      !connectAppointmentLoading &&
      !connectScheduleLoading
    ) {
      // Delay hiding the loader until after the next repaint / wait till react has updated the ui
      requestAnimationFrame(() => {
        setIsLoading(false);
      });
    } else {
      // Show the loader when data is fetching
      setIsLoading(true);
    }
  }, [
    isFetching,
    planAppointmentsLoading,
    connectAppointmentLoading,
    connectScheduleLoading,
    queryLoading
  ]);

  return {
    data,
    kidsRatio,
    planningEntities: planningEntities,
    setPlanningEntities: setPlanningEntities,
    clonedPlanningEntities: clonedPlanningEntitiesRef.current,
    setClonedPlanningEntities: setClonedPlanningEntities,
    planAppointments,
    connectAppointmentGrouping,
    connectSchedule,
    revertAction,
    startDate: startDate as string,
    isLoading,
    employees,
    showEvents
  };
}
